import { useEffect, useState } from 'react';
import {
  Box, Container, Divider, IconButton, Tooltip, Snackbar, Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShareIcon from '@mui/icons-material/Share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Spinner } from '../../components/index';
import {
  getReaderCardFocus,
  loadMedia,
  useUser,
  theme,
  getSocialMedia,
  getRating,
  getCertificates,
  getLanguages,
} from '../../lib/index';
import About from './About';
import Offers from './Offers';
import Vision from './Vision';
import Certificates from './Certificates';
import Reviews from './Reviews';
import GoBackButton from './GoBackButton';

const extractAboutData = (userData) => {
  const {
    reader_profile_focus_genre_private: genrePrivate,
    reader_profile_focus_genre_business: genreBusiness,
  } = userData.acf.reader_profile_focus_genre;

  const {
    reader_profile_focus_expertise_coaching: focusCoaching,
    reader_profile_focus_expertise_digital: focusDigital,
    reader_profile_focus_expertise_mediation: focusMediation,
  } = userData.acf.reader_profile_focus_expertise;

  const {
    reader_profile_focus_hdfocus_business: focusBusiness,
    reader_profile_focus_hdfocus_career: focusCareer,
    reader_profile_focus_hdfocus_family: focusFamily,
    reader_profile_focus_hdfocus_love: focusLove,
    reader_profile_focus_hdfocus_additional: additionalFocus,
  } = userData.acf.reader_profile_focus_hdfocus;

  return {
    paused: userData.acf.reader_calendar_pause,
    inactive: userData.acf.reader_calendar_inactive,
    name: userData.acf.reader_profile_description_fullname,
    location: userData.acf.reader_profile_description_location,
    description: userData.acf.reader_profile_description_introduction,
    rating: getRating(userData),
    ratingCount: userData.acf.reader_rating && userData.acf.reader_rating.length,
    socialMedia: getSocialMedia(userData),
    certificates: getCertificates(userData),
    focus: getReaderCardFocus(userData),
    calendly: userData.acf.reader_calendar_link,
    genrePrivate,
    genreBusiness,
    focusCoaching,
    focusDigital,
    focusMediation,
    focusBusiness,
    focusCareer,
    focusFamily,
    focusLove,
    additionalFocus,
    languages: getLanguages(userData),
    audio: null,
    image: null,
  };
};

const extractOffersData = (userData) => ({
  offers: userData.acf.reader_offers,
  shopifyId: userData.acf.reader_offers_shopifyproductid,
  buyButton: userData.buy_button,
});

const extractVisionData = (userData) => ({
  vision: userData.acf.reader_profile_focus_vision,
  detail: userData.acf.reader_profile_focus_visiondetail,
  video: userData.acf.reader_media_videolink,
});

const extractReviewData = (userData) => ({
  review: userData.acf.reader_rating,
  averageRating: getRating(userData),
});

function Detail({ readerId, comingFromOutside }) {
  const user = useUser(readerId);
  const [image, setImage] = useState({ id: '', src: '', alt: '' });
  const [audio, setAudio] = useState({ id: '', src: '', alt: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const audioSample = user.data?.acf.reader_media_audiosample;
  const descriptionPhoto = user.data?.acf.reader_profile_description_photo;

  // map data to screen
  useEffect(() => {
    if (!user.loading && !user.error && user.data) {
      loadMedia(audioSample).then(setAudio);
      loadMedia(descriptionPhoto).then(setImage);
    }
  }, [user, audioSample, descriptionPhoto]);

  const aboutData = user.data ? { ...extractAboutData(user.data), image, audio } : {};
  const visionData = user.data ? extractVisionData(user.data) : {};
  const offersData = user.data ? extractOffersData(user.data) : {};
  const certificateData = user.data?.acf.reader_profile_certificates;
  const reviewData = user.data ? extractReviewData(user.data) : {};

  if (user.loading) {
    return <Spinner />;
  }

  if (user.error || aboutData?.paused || aboutData?.inactive) {
    return <UserError comingFromOutside={comingFromOutside} />;
  }

  return (
    <Wrapper id="detail">
      <TopFlexbox>
        <GoBackButton {...{ comingFromOutside }} />
        <CopyToClipboard text={`https://member.humandesignclub.com/reader/?coisar_reader=${readerId}`}>
          <IconButton onClick={() => setSnackbarOpen(true)}>
            <Tooltip title="Link in die Zwischenablage kopieren">
              <ShareIcon />
            </Tooltip>
          </IconButton>
        </CopyToClipboard>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={snackbarOpen}
          autoHideDuration={3000}
          message="Der Link wurde in die Zwischenablage kopiert."
          onClose={() => setSnackbarOpen(false)}
        />
      </TopFlexbox>
      <>
        { aboutData.name
          && (
          <>
            <About aboutData={aboutData} />
            <HorizontalDivider />
          </>
          )}
        { !!visionData?.vision
          && (
          <>
            <Vision visionData={visionData} />
            <HorizontalDivider />
          </>
          )}
        { !!offersData?.offers
          && (
          <>
            <Offers offersData={offersData} />
            <HorizontalDivider />
          </>
          )}
        { certificateData?.length > 1
          && (
          <>
            <Certificates certificateData={certificateData} />
            <HorizontalDivider />
          </>
          )}
        { !!reviewData?.review
          && <Reviews reviewData={reviewData.review} averageRating={reviewData.averageRating} />}
      </>
    </Wrapper>
  );
}

export default Detail;

function UserError({ comingFromOutside }) {
  return (
    <>
      <UserErrorContainer>
        <UserErrorTitleBox>
          <UserErrorTitleIcon />
          <UserErrorTitleLabel variant="h3">
            Ein unerwarteter Fehler ist aufgetreten!
          </UserErrorTitleLabel>
        </UserErrorTitleBox>

        <Typography>
          Das angeforderte Profil ist zur Zeit leider nicht verfügbar.
        </Typography>
      </UserErrorContainer>

      <GoBackButton comingFromOutside={comingFromOutside} />
    </>
  );
}

const UserErrorContainer = styled(Box, {})({
  maxWidth: 600,
  padding: '28px 35px',
  border: `2px solid ${theme.palette.red}`,
  margin: '16px 16px 32px',

  '@media (min-width: 706px)': {
    margin: '16px auto 32px',
  },
});

const UserErrorTitleIcon = styled(ReportProblemIcon, {})({
  color: theme.palette.red,
});

const UserErrorTitleBox = styled(Box, {})({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 8,
});

const UserErrorTitleLabel = styled(Typography, {})({
  marginLeft: 8,
});

// Styling

const Wrapper = styled(Container, {})({
  display: 'flex',
  overflow: 'hidden',
  padding: 10,
  maxWidth: '90vw',

  flexDirection: 'column',

  background: theme.palette.backgroundgrey,

  [theme.breakpoints.up('laptop')]: {
    display: 'block',
    padding: 40,
    maxWidth: 1200,
  },
});

const HorizontalDivider = styled(Divider, {})({
  margin: '50px 0',
});

const TopFlexbox = styled(Container, {})({
  display: 'flex',
  marginBottom: 50,

  gap: 10,
  justifyContent: 'space-between',
  alignItems: 'center',

  '& .MuiSvgIcon--root': {
    fontSize: '2.5rem',
  },

  [theme.breakpoints.up('laptop')]: {
    gap: 50,
  },
});
